<script setup lang="ts">
/**
 * The one search field (MAR-948, wave 2; guard `tests/unit/viewControlsGuard.test.ts`).
 *
 * `v-model` holds the text. `input` fires on each keystroke (callers that debounce a server search
 * listen to it); `clear` fires when the ✕ empties the box, after the model is already `''`, so a
 * caller that runs its search immediately on clear does not wait for a debounce. The debounce itself
 * stays with the caller: one screen hits the server, another filters in memory.
 *
 * `type="search"` gives the box the `searchbox` role; the browser's own cancel glyph is hidden so
 * the field never shows two ✕s. `:clearable="false"` drops the ✕ for a box that must not offer one.
 *
 * `class`/`style` style the WRAPPER (width, flex); every other attribute and listener — `autofocus`,
 * `@keyup.enter`, `@focus`, `data-testid` — lands on the input itself. `focus()` is exposed.
 *
 * `variant="bare"` drops the border and background, for a box that sits inside an already bordered
 * row (a picker header, a scope panel).
 *
 * Slots: `icon` replaces the magnifier (a spinner while searching); `trailing` adds controls after
 * the clear button (a submit button) — pad the input for them with `input-class`.
 */
import { computed, ref, useAttrs, type HTMLAttributes } from 'vue'
import { Search, X } from 'lucide-vue-next'
import { cn } from '~/lib/utils'

defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<{
  modelValue: string
  /** Defaults to the i18n "search". Also the box's accessible name unless `label` is given. */
  placeholder?: string
  label?: string
  /** `xs` 28px (sidebar rail) · `sm` 32px · `md` 36px (page toolbars) · `lg` 48px (a hero search). */
  size?: 'xs' | 'sm' | 'md' | 'lg'
  variant?: 'boxed' | 'bare'
  clearable?: boolean
  inputClass?: HTMLAttributes['class']
}>(), { size: 'md', variant: 'boxed', clearable: true })

const emit = defineEmits<{
  'update:modelValue': [value: string]
  input: [value: string]
  clear: []
}>()

const { t } = useI18n()
const attrs = useAttrs()
const inputEl = ref<HTMLInputElement | null>(null)

const SIZES = {
  xs: { input: 'h-7 pl-7 pr-7 text-xs', left: 'left-2', icon: 'h-3.5 w-3.5', right: 'pr-1.5', clear: 'h-4 w-4', x: 'h-3 w-3' },
  sm: { input: 'h-8 pl-8 pr-8 text-xs', left: 'left-2.5', icon: 'h-3.5 w-3.5', right: 'pr-2', clear: 'h-4 w-4', x: 'h-3 w-3' },
  md: { input: 'h-9 pl-9 pr-8 text-sm', left: 'left-3', icon: 'h-4 w-4', right: 'pr-2', clear: 'h-5 w-5', x: 'h-3.5 w-3.5' },
  lg: { input: 'h-12 pl-10 pr-10 text-sm', left: 'left-3', icon: 'h-4 w-4', right: 'pr-3', clear: 'h-6 w-6', x: 'h-4 w-4' },
} as const
const VARIANTS = {
  boxed: 'rounded-md border border-input bg-background focus-visible:ring-1 focus-visible:ring-ring',
  bare: 'border-0 bg-transparent',
} as const

const size = computed(() => SIZES[props.size])
const placeholderText = computed(() => props.placeholder ?? t('common.search'))
const inputAttrs = computed(() => {
  const { class: _class, style: _style, ...rest } = attrs
  return rest
})

function onInput(event: Event) {
  const value = (event.target as HTMLInputElement).value
  emit('update:modelValue', value)
  emit('input', value)
}

function clear() {
  emit('update:modelValue', '')
  emit('clear')
  inputEl.value?.focus()
}

defineExpose({ focus: () => inputEl.value?.focus() })
</script>

<template>
  <div class="relative" :class="attrs.class as HTMLAttributes['class']" :style="attrs.style as any">
    <span class="pointer-events-none absolute top-1/2 flex -translate-y-1/2 items-center text-muted-foreground" :class="size.left">
      <slot name="icon">
        <Search :class="size.icon" />
      </slot>
    </span>
    <input
      v-bind="inputAttrs"
      ref="inputEl"
      type="search"
      :value="modelValue"
      :placeholder="placeholderText"
      :aria-label="label ?? placeholderText"
      :class="cn(
        'w-full text-foreground placeholder:text-muted-foreground focus-visible:outline-none [&::-webkit-search-cancel-button]:appearance-none',
        VARIANTS[variant],
        size.input,
        props.inputClass,
      )"
      @input="onInput"
    >
    <div class="absolute inset-y-0 right-0 flex items-center gap-2" :class="size.right">
      <button
        v-if="clearable && modelValue"
        type="button"
        class="flex items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-foreground"
        :class="size.clear"
        :title="t('common.clearSearch')"
        :aria-label="t('common.clearSearch')"
        @click="clear"
      >
        <X :class="size.x" />
      </button>
      <slot name="trailing" />
    </div>
  </div>
</template>
